import { Alert } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { find, isEmpty, map, size, uniq } from 'lodash';
import pluralize from 'pluralize';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { ResponseError } from 'superagent';

import {
  applyBulkSlideTags,
  BulkSlideTagApplyResponse,
  BulkSlideTagOperation,
  BulkSlideTagResultScopeMode,
  BulkSlideTagSelectionResponse,
  BulkSlideTagSummary,
  getBulkSlideTagSelection,
  getBulkSlideTagSummary,
} from 'api/slideTags';
import { ResultsMode } from 'interfaces/experimentResults';
import { ProcedureResponse } from 'interfaces/procedure';
import { Slide } from 'interfaces/slide';
import { SlideTag } from 'interfaces/slideTag';
import { getSelectedAwareTagOptions, getValidSelectedAwareTags } from 'utils/getSelectedAwareTagOptions';
import { useOrchestrations } from 'utils/queryHooks/useOrchestrations';
import useSlideTagOptions from 'utils/queryHooks/useSlideTagOptions';
import { CasesParams } from 'utils/useCasesParams/types';
import { ActionStepper } from '../ActionStepper';
import { BulkSlideTagConfigureStep } from './BulkSlideTagConfigureStep';
import { BulkSlideTagSummaryReview } from './BulkSlideTagSummaryReview';
import {
  getBulkSlideTagErrorMessage,
  getHydratedSelectedSlidesById,
  getSlideFromSelection,
  hasExperimentResultSpecificTags,
  hasTargetedSlideSelection,
  operationRequiresTags,
  useBulkSlideTagRequest,
} from './utils';

interface BulkSlideTaggingDialogProps {
  casesParams: CasesParams;
  onClose: () => void;
}

export const BulkSlideTaggingDialog: React.FC<BulkSlideTaggingDialogProps> = ({ casesParams, onClose }) => {
  const queryClient = useQueryClient();
  const activeFilterOrchestrationId = casesParams.filters?.orchestrationId;
  const [activeStep, setActiveStep] = React.useState(0);
  const [isStepFailed, setIsStepFailed] = React.useState<Record<number, boolean>>({});
  const [operation, setOperation] = React.useState<BulkSlideTagOperation>('add');
  const [selectedTags, setSelectedTags] = React.useState<SlideTag[]>([]);
  const [resultScopeMode, setResultScopeMode] = React.useState<BulkSlideTagResultScopeMode>(
    activeFilterOrchestrationId ? 'filter' : 'approved_per_slide'
  );
  const [explicitOrchestrationId, setExplicitOrchestrationId] = React.useState<string | undefined>();
  const needsExperimentResultScope = operationRequiresTags(operation) && hasExperimentResultSpecificTags(selectedTags);
  const effectiveResultScopeMode: BulkSlideTagResultScopeMode = needsExperimentResultScope ? resultScopeMode : 'filter';
  const { slideTagOptions = [], isLoading: isLoadingSlideTagOptions } = useSlideTagOptions();
  const { data: orchestrationsData, isLoading: isLoadingOrchestrations } = useOrchestrations({
    studyId: casesParams.studyId || casesParams.filters?.studyId,
    resultsMode: ResultsMode.Manual,
    enabled: needsExperimentResultScope && resultScopeMode === 'explicit',
  });
  const orchestrationOptions = orchestrationsData?.orchestrations || [];
  const studyId = casesParams.studyId || casesParams.filters?.studyId;
  const selectionQuery = useQuery<BulkSlideTagSelectionResponse, ResponseError>({
    queryKey: ['bulkSlideTagSelection', casesParams],
    queryFn: ({ signal }) => getBulkSlideTagSelection(casesParams, signal),
    enabled: Boolean(studyId) && hasTargetedSlideSelection(casesParams),
    retry: false,
  });
  const resolvedSlideIds = React.useMemo(
    () => uniq(map(selectionQuery.data?.slides || [], 'slideId')),
    [selectionQuery.data?.slides]
  );
  const selectedScopeSlides = React.useMemo<Slide[]>(
    () => map(selectionQuery.data?.slides || [], getSlideFromSelection),
    [selectionQuery.data?.slides]
  );
  const previewSlideIds = React.useMemo(
    () => map(selectionQuery.data?.previewSlides || [], 'slideId'),
    [selectionQuery.data?.previewSlides]
  );
  const allSelectedSlidesHaveNoTags =
    selectedScopeSlides.length > 0 && selectedScopeSlides.every((slide) => isEmpty(slide.slideTagAssignments || []));
  const selectedAwareSlideTagOptions = React.useMemo(
    () =>
      operationRequiresTags(operation) && selectedScopeSlides.length > 0
        ? getSelectedAwareTagOptions({
            tagOptions: slideTagOptions,
            selectedItems: selectedScopeSlides,
            tagAction: operation,
            getOptionKey: (tagOption) => tagOption.id,
            getSelectedItemTagKeys: (slide) =>
              map(slide.slideTagAssignments || [], (assignment) => assignment.slideMetadataTag?.id || assignment.tagId),
          })
        : slideTagOptions,
    [operation, selectedScopeSlides, slideTagOptions]
  );
  const requestCasesParams = React.useMemo<CasesParams>(
    () => ({
      ...casesParams,
      slideIdsToInclude: resolvedSlideIds,
      slideIdsToExclude: undefined,
    }),
    [casesParams, resolvedSlideIds]
  );
  const request = useBulkSlideTagRequest({
    casesParams: requestCasesParams,
    operation,
    selectedTags,
    resultScopeMode: effectiveResultScopeMode,
    orchestrationId: explicitOrchestrationId,
  });
  const canRequestSummary =
    hasTargetedSlideSelection(casesParams) &&
    size(resolvedSlideIds) > 0 &&
    (!operationRequiresTags(operation) || !isEmpty(selectedTags)) &&
    (!needsExperimentResultScope || effectiveResultScopeMode !== 'explicit' || Boolean(explicitOrchestrationId));
  const summaryQuery = useQuery<BulkSlideTagSummary, ResponseError>(
    ['bulkSlideTagSummary', request],
    ({ signal }) => getBulkSlideTagSummary(request, signal),
    { enabled: activeStep === 1 && canRequestSummary, retry: false }
  );
  const summary = summaryQuery.data;
  const reviewWouldBeNoOp = Boolean(summary) && summary.changedSlideCount === 0;
  const isReviewStepLoading =
    activeStep === 1 && canRequestSummary && (summaryQuery.isLoading || summaryQuery.isFetching);
  const hydratedSelectedSlidesById = React.useMemo(
    () =>
      getHydratedSelectedSlidesById({
        selectedSlides: selectedScopeSlides,
        procedureResponses: queryClient
          .getQueryCache()
          .findAll(['procedures'])
          .map((query) => query.state.data as ProcedureResponse | undefined),
      }),
    [queryClient, selectedScopeSlides]
  );
  const applyMutation = useMutation<BulkSlideTagApplyResponse, ResponseError, typeof request>({
    mutationFn: (variables) => applyBulkSlideTags(variables),
    onSuccess: (response) => {
      enqueueSnackbar(
        `Updated slide tags for ${response.changedSlideCount} ${pluralize('slide', response.changedSlideCount)}`,
        { variant: 'success' }
      );
      queryClient.invalidateQueries(['procedures']);
      queryClient.invalidateQueries(['slideTags']);
      queryClient.invalidateQueries(['slideThumbnailsData']);
      onClose();
    },
  });

  React.useEffect(() => {
    setIsStepFailed((currentStepFailures) =>
      Object.keys(currentStepFailures).length === 0 ? currentStepFailures : {}
    );

    if (applyMutation.status !== 'idle') {
      applyMutation.reset();
    }
  }, [applyMutation.reset, applyMutation.status, request]);

  React.useEffect(() => {
    if (activeStep === 1 && summaryQuery.isError) setIsStepFailed({ 1: true });
  }, [activeStep, summaryQuery.isError]);

  React.useEffect(() => {
    if (activeStep !== 1 || summaryQuery.isLoading || summaryQuery.isFetching || summaryQuery.isError) return;
    setIsStepFailed(summary && summary.eligibleSlideCount === 0 ? { 1: true } : {});
  }, [activeStep, summary, summaryQuery.isError, summaryQuery.isFetching, summaryQuery.isLoading]);

  React.useEffect(() => {
    if (!needsExperimentResultScope) setExplicitOrchestrationId(undefined);
  }, [needsExperimentResultScope]);

  React.useEffect(() => {
    if (allSelectedSlidesHaveNoTags && (operation === 'remove' || operation === 'clear')) {
      setOperation('add');
      setSelectedTags([]);
    }
  }, [allSelectedSlidesHaveNoTags, operation]);

  React.useEffect(() => {
    if (!operationRequiresTags(operation) || selectedScopeSlides.length === 0) {
      return;
    }

    setSelectedTags((currentSelectedTags) => {
      const validSelectedTags = getValidSelectedAwareTags({
        tags: currentSelectedTags,
        tagOptions: slideTagOptions,
        selectedItems: selectedScopeSlides,
        tagAction: operation,
        getOptionKey: (tagOption) => tagOption.id,
        getSelectedItemTagKeys: (slide) =>
          map(slide.slideTagAssignments || [], (assignment) => assignment.slideMetadataTag?.id || assignment.tagId),
      });

      return validSelectedTags.length === currentSelectedTags.length ? currentSelectedTags : validSelectedTags;
    });
  }, [operation, selectedScopeSlides, slideTagOptions]);

  const validateStep = async () => {
    if (activeStep === 0) {
      if (!canRequestSummary) {
        setIsStepFailed({ 0: true });
        window.setTimeout(() => setActiveStep(0), 0);
        return false;
      }
      setIsStepFailed({});
      return true;
    }
    if (activeStep === 1) {
      if (!summary || summary.eligibleSlideCount === 0) {
        setIsStepFailed({ 1: true });
        return false;
      }
      try {
        await applyMutation.mutateAsync(request);
        setIsStepFailed({});
        return true;
      } catch {
        setIsStepFailed({ 1: true });
        return false;
      }
    }
    return true;
  };

  return (
    <ActionStepper
      activeStateHandlers={[activeStep, setActiveStep]}
      setActiveStepForValidation={setActiveStep}
      validateStep={validateStep}
      handleSubmit={() => undefined}
      finishText="Apply"
      finishDisabled={activeStep === 1 && reviewWouldBeNoOp}
      finishDisabledMessage={
        activeStep === 1 && reviewWouldBeNoOp ? 'This selection will not change any slide tags.' : undefined
      }
      isStepFailed={isStepFailed}
      incompleteStepsMessage={isReviewStepLoading ? false : undefined}
      steps={[
        {
          label: 'Configure',
          content: (
            <BulkSlideTagConfigureStep
              hasExplicitSlideSelection={hasTargetedSlideSelection(casesParams)}
              operation={operation}
              setOperation={setOperation}
              selectedTags={selectedTags}
              setSelectedTags={setSelectedTags}
              slideTagOptions={selectedAwareSlideTagOptions}
              isLoadingSlideTagOptions={isLoadingSlideTagOptions}
              needsExperimentResultScope={needsExperimentResultScope}
              resultScopeMode={resultScopeMode}
              setResultScopeMode={setResultScopeMode}
              activeFilterOrchestrationId={activeFilterOrchestrationId}
              explicitOrchestrationId={explicitOrchestrationId}
              setExplicitOrchestrationId={setExplicitOrchestrationId}
              orchestrationOptions={orchestrationOptions}
              isLoadingOrchestrations={isLoadingOrchestrations}
              operationRequiresTags={operationRequiresTags}
              disableRemove={allSelectedSlidesHaveNoTags}
              disableClear={allSelectedSlidesHaveNoTags}
            />
          ),
        },
        {
          label: 'Review',
          isLoading: isReviewStepLoading,
          content: (
            <>
              {summaryQuery.isError && (
                <Alert severity="error">{getBulkSlideTagErrorMessage(summaryQuery.error)}</Alert>
              )}
              <BulkSlideTagSummaryReview
                summary={summary}
                selectedTags={selectedTags}
                hydratedSlidesById={hydratedSelectedSlidesById}
                previewSlideIds={previewSlideIds}
                selectedSlideCount={resolvedSlideIds.length}
                previewLimit={selectionQuery.data?.previewLimit || previewSlideIds.length}
                isLoadingExpectedResults={isReviewStepLoading}
                studyId={casesParams.studyId || casesParams.filters?.studyId}
              />
            </>
          ),
          subLabel:
            resultScopeMode === 'explicit'
              ? find(orchestrationOptions, { orchestrationId: explicitOrchestrationId })?.orchestrationId
              : undefined,
        },
      ]}
    />
  );
};
